export interface CustomSelectProps<T> {
  options: T[];
  selectedOption: T | null;
  onChange: (option: T | null) => void;
  formatOptionLabel?: (option: T) => React.JSX.Element;
  placeholder?: string;
  id?: string;
}

export interface CustomAsyncSelectProps {
  onChange: (option: any) => void;
  loadOptions: (searchValue: string, callback: any) => any;
  placeholder: string;
  noOptionsMessage?: () => string;
  loadingMessage?: () => string;
  id?: string;
}

export enum CustomAsyncSelectResultType {
  CONDIMENT = "condiment",
  MIX = "mix",
}

interface Condiment {
  id: number;
  name: string;
  description: string;
  history: string;
  recommendation: string;
  minWeight: number;
  maxWeight: number;
  minDensity: number;
  medDensity: number;
  maxDensity: number;
  active: boolean;
  price: number;
  image: string;
  condimentTypeId: number;
  allergic: boolean;
  createdAt: string;
  hidden: boolean;
  categories: {
    id: number;
    name: string;
    parentId: number | null;
    active: boolean;
    order: number;
  }[];
  geoOrigins: any[]; // Not specified in the provided data
  tasteAreas: any[]; // Not specified in the provided data
  tasteMoods: any[]; // Not specified in the provided data
  botanicalFamilies: {
    id: number;
    name: string;
  }[];
  botanicalOrigins: {
    id: number;
    name: string;
  }[];
  latinNames: {
    id: number;
    name: string;
  }[];
  benefits: any[]; // Not specified in the provided data
  condimentType: {
    id: number;
    name: string;
  };
}

interface Account {
  id: number;
  firstName: string;
  lastName: string;
  picture: string | null;
  rating: {
    id: number;
    value: number;
    specialPoints: number;
    valueWithoutSpecialPoints: number;
  };
}

interface CondimentMix {
  id: number;
  condimentId: number;
  mixId: number;
  weight: number | null;
  parts: number;
  percentage: number;
  condiment: Condiment;
}

interface Category {
  id: number;
  name: string;
  parentId: number | null;
  active: boolean;
  order: number;
}

interface MixData {
  id: number;
  name: string;
  accountId: number | null;
  public: boolean;
  weight: number;
  code: string;
  description: null | string;
  image: string;
  created: string;
  category: Category;
  tags: any[];
  account: Account | null;
  condimentMixes: CondimentMix[];
  categories: Category[];
  orders: number;
}

interface Mix {
  type: CustomAsyncSelectResultType.MIX;
  data: MixData;
}

interface CondimentData {
  id: number;
  name: string;
  description: string;
  history: string;
  recommendation: string;
  minWeight: number;
  maxWeight: number;
  minDensity: number;
  medDensity: number;
  maxDensity: number;
  active: boolean;
  price: number;
  image: string;
  condimentTypeId: number;
  allergic: boolean;
  createdAt: string;
  hidden: boolean;
  categories: Category[];
  geoOrigins: any[]; // Not specified in the provided data
  tasteAreas: any[]; // Not specified in the provided data
  tasteMoods: any[]; // Not specified in the provided data
  botanicalFamilies: {
    id: number;
    name: string;
  }[];
  botanicalOrigins: {
    id: number;
    name: string;
  }[];
  latinNames: {
    id: number;
    name: string;
  }[];
  benefits: any[]; // Not specified in the provided data
  condimentType: {
    id: number;
    name: string;
  };
}

interface CondimentItem {
  type: CustomAsyncSelectResultType.CONDIMENT;
  data: CondimentData;
}

export interface AsyncCondiment extends CondimentData {
  type: CustomAsyncSelectResultType.CONDIMENT;
}
export interface AsyncMix extends MixData {
  type: CustomAsyncSelectResultType.MIX;
}

export type CustomAsyncSelectResponse = Mix | CondimentItem;
